import ErrorTrack from "../../error_track"
import { checkIfResizeNeeded, resizeImage } from "./image_resize"

const KB = 1024

interface ImageValidationOptions {
  allowedTypes?: string[]
  maxUploadSizeKB?: number
  sizeThresholdKB?: number
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

export type ImageProcessResult =
  | { status: "success"; file: File; resized: boolean }
  | { status: "empty" }
  | { status: "invalid_type" }
  | { status: "file_too_large"; maxSizeKB: number; actualSizeKB: number }
  | { status: "resize_failed" }

// Sanitise the size and type of file, resize if needed - can be skipped by setting sizes to 0
export async function processImageFile(
  file: File | null | undefined,
  options: ImageValidationOptions = {},
): Promise<ImageProcessResult> {
  const {
    allowedTypes = ["image/jpeg", "image/png", "image/gif"],
    maxUploadSizeKB = 15 * 1024, // 15MB
    sizeThresholdKB = 1024, // 1MB
    maxWidth = 1024,
    maxHeight = 1024,
    quality = 0.8,
  } = options

  // validate the inputs
  const validatedMaxUploadSizeKB = Math.max(maxUploadSizeKB, 1)
  const validatedSizeThresholdKB = Math.max(sizeThresholdKB, 0)

  const maxUploadSize = validatedMaxUploadSizeKB * KB
  const sizeThreshold = validatedSizeThresholdKB * KB

  // Check if file exists
  if (!file) return { status: "empty" }

  // Type validation
  if (!allowedTypes.includes(file.type)) return { status: "invalid_type" }

  // Validate that the original is smaller than the max limit
  if (file.size > maxUploadSize)
    return {
      status: "file_too_large",
      maxSizeKB: validatedMaxUploadSizeKB,
      actualSizeKB: Math.ceil(file.size / KB),
    }

  // Check if resizing would be beneficial
  const needsResize = await checkIfResizeNeeded(file, maxWidth, maxHeight, sizeThreshold)

  if (needsResize && file.type !== "image/gif") {
    try {
      const resizedFile = await resizeImage(file, maxWidth, maxHeight, quality)
      return { status: "success", file: resizedFile, resized: true }
    } catch (error) {
      ErrorTrack.notify(error as Error, {
        context: "image_resize",
        fileName: file.name,
        fileSize: file.size,
      })
      return { status: "resize_failed" }
    }
  }

  return { status: "success", file, resized: false }
}
